import { Injectable, inject, signal, computed, effect } from '@angular/core';
import {
  SessionEntities,
  SessionContextEntity,
  SessionPayload,
  PendingFollowUp,
  VoiceChatMessage,
  VoiceMessageOption,
  VoiceMessageLink,
  VoiceConfirmationCard,
  VoiceSessionSummary,
  mapVoiceMessageRowsToChatMessages,
  mapVoiceSessionRowToEntities,
  mapVoiceSessionRowToPendingFollowUp
} from '../models';
import { AuthService } from '../../auth/auth.service';
import { VoiceBriefingService } from './voice-briefing.service';
import { VoiceSessionStoreService } from './voice-session-store.service';

/** Nombre max d'échanges transmis dans l'historique du contrat Edge Function */
const MAX_HISTORY_ENTRIES = 8;

/** Longueur max (caractères) du `content` d'une entrée d'historique transmise */
const MAX_HISTORY_ENTRY_CHARS = 280;

/** Budget total (caractères) cumulés des `content` de l'historique transmis */
const MAX_HISTORY_TOTAL_CHARS = 2000;

/** Longueur max (caractères) du titre auto-généré d'une session persistée */
const MAX_SESSION_TITLE_CHARS = 80;

/**
 * Tronque un texte à `max` caractères, en ajoutant `…` si tronqué
 */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * Service de session conversationnelle de l'assistant vocal.
 * Mémorise le fil de messages, les entités résolues (dernière tâche/projet)
 * et la question de suivi en attente, pour les transmettre à l'Edge Function
 * `voice-command` à chaque échange (cf. contrat `session`, MOKA-204).
 *
 * Phase 2 (MOKA-205) : la session locale (signals) reste la source de vérité
 * pour l'UI — les composants ne changent pas. En parallèle, ce service
 * persiste silencieusement la conversation via `VoiceSessionStoreService` :
 * - lazy creation : la ligne `voice_sessions` n'existe qu'à partir du premier
 *   message `user` (pas de session vide créée à l'ouverture de l'overlay) ;
 * - toute écriture réseau (création, insertion de message, update de session)
 *   est fire-and-forget côté appelant (jamais de `await` qui bloquerait le
 *   fil de conversation) et catchée silencieusement (`console.warn`) — une
 *   panne réseau ne doit jamais casser le chat ;
 * - `loadSession()` fait exception : c'est un chargement explicite demandé
 *   par l'utilisateur (reprise d'historique), son échec doit remonter à
 *   l'appelant pour affichage d'un état d'erreur.
 *
 * Gestion de la course session-en-cours-de-création : `#sessionCreation`
 * mémorise la Promise de création en vol ; un message arrivant avant sa
 * résolution s'enchaîne dessus au lieu d'en déclencher une seconde.
 *
 * Gestion des résolutions tardives après `reset()`/`loadSession()` : un
 * compteur `#generation` est incrémenté à chaque rupture de continuité de la
 * conversation ; la résolution d'une création lancée avant cette rupture est
 * ignorée si elle arrive après (pas d'AbortController, simple comparaison).
 *
 * MOKA-206 Lot F : `ignoredPageContext` (types de contexte de page écartés
 * par l'utilisateur via les chips de contexte) est VOLATILE — non persisté,
 * repart à vide à `reset()`/`loadSession()` (une reprise de session ne
 * restaure PAS les contextes de page ignorés : comportement accepté, cf.
 * `ignoredPageContext` plus bas).
 */
@Injectable({ providedIn: 'root' })
export class VoiceSessionService {
  #auth = inject(AuthService);
  #briefing = inject(VoiceBriefingService);
  #store = inject(VoiceSessionStoreService);

  #messages = signal<VoiceChatMessage[]>([]);
  messages = computed(() => this.#messages());

  #entities = signal<SessionEntities>({});
  entities = computed(() => this.#entities());

  #pendingFollowUp = signal<PendingFollowUp | null>(null);
  pendingFollowUp = computed(() => this.#pendingFollowUp());

  /**
   * Types de contexte de PAGE écartés par choix utilisateur (croix sur un chip
   * de contexte "source page" — cf. `VoiceContextChipsComponent`, MOKA-206
   * Lot F). VOLATILE — en mémoire uniquement, jamais persisté (même compromis
   * que `VoiceChatMessage.options`, cf. sa JSDoc) : la table `voice_sessions`
   * n'a pas de colonne pour ça, et il ne s'agit pas d'un fait à restaurer (une
   * reprise de session repart avec le contexte de page pleinement autorisé).
   * Volontairement ICI et pas dans `VoiceContextService`, qui reste une
   * lecture pure de l'URL/page : ce flag est un choix utilisateur SESSION.
   */
  #ignoredPageContext = signal<ReadonlySet<'project' | 'calendar'>>(new Set());
  ignoredPageContext = computed(() => this.#ignoredPageContext());

  #persistedSessionId = signal<string | null>(null);
  persistedSessionId = computed(() => this.#persistedSessionId());

  /** Promise de création de session en vol (`null` si aucune création en cours) */
  #sessionCreation: Promise<string> | null = null;

  /** Incrémenté à chaque rupture de continuité (`reset()`, `loadSession()`) */
  #generation = 0;

  /**
   * true dès qu'une saisie SIGNIFICATIVE (texte tapé/transcript vocal, jamais
   * un clic bouton) a fixé le titre — alors plus jamais réécrit (ni par une
   * autre saisie significative ultérieure, ni par `suggestFlowTitle`)
   */
  #titleLocked = false;

  /**
   * Titre suggéré par un flow guidé (`suggestFlowTitle`) en attendant qu'une
   * session existe pour le recevoir — cf. `#persistMessage`
   */
  #pendingFlowTitle: string | null = null;

  /**
   * Réinitialise la session à chaque changement de profil (connexion,
   * déconnexion, changement d'organisation) pour éviter qu'une conversation
   * ne fuite d'un compte à l'autre. `effect()` s'exécute une première fois
   * immédiatement au bootstrap du service — sans conséquence : la session
   * est déjà vide à ce moment, `reset()` y est un no-op visible.
   */
  constructor() {
    effect(() => {
      this.#auth.profileRevision();
      this.reset();
      // L'accroche d'ouverture est propre à un utilisateur : la garder après un
      // changement de compte afficherait les retards de quelqu'un d'autre.
      this.#briefing.reset();
    });
  }

  /**
   * Ajoute un message au fil de conversation et déclenche sa persistance
   * (lazy creation de session au premier message `user`, puis insertion).
   *
   * `options` (boutons de réponse rapide, MOKA-206 Phase 3) n'alimente QUE
   * l'objet poussé dans le signal local `#messages` : il n'est jamais passé à
   * `#persistMessage`/`#insertMessage`, qui continuent d'écrire exactement
   * `content`/`role`/`inputMode` comme avant. Voir la JSDoc de
   * `VoiceChatMessage.options` pour le pourquoi (pas de colonne `voice_messages`
   * pour ça, compromis Phase 3 assumé).
   */
  addMessage(
    role: 'user' | 'assistant',
    content: string,
    inputMode: 'voice' | 'text',
    options?: VoiceMessageOption[],
    titleEligible: boolean = true,
    links?: VoiceMessageLink[],
    confirmation?: VoiceConfirmationCard
  ): void {
    const message: VoiceChatMessage = { role, content, inputMode, timestamp: Date.now() };
    if (confirmation) {
      message.confirmation = confirmation;
    }
    if (options?.length) {
      message.options = options;
    }
    if (links?.length) {
      message.links = links;
    }
    this.#messages.update(msgs => [...msgs, message]);
    this.#persistMessage(role, content, inputMode, titleEligible);
  }

  /**
   * Construit le payload `session` attendu par le contrat de l'Edge Function.
   * Trois garde-fous indépendants s'appliquent à l'historique transmis :
   * nombre max d'entrées, longueur max par entrée, budget total de caractères.
   */
  buildSessionPayload(): SessionPayload {
    const capped = this.#messages().slice(-MAX_HISTORY_ENTRIES);
    let history = capped.map(({ role, content }) => ({
      role,
      content: truncate(content, MAX_HISTORY_ENTRY_CHARS)
    }));

    let totalChars = history.reduce((sum, h) => sum + h.content.length, 0);
    while (totalChars > MAX_HISTORY_TOTAL_CHARS && history.length > 0) {
      totalChars -= history[0].content.length;
      history = history.slice(1);
    }

    const payload: SessionPayload = { entities: this.#entities(), history };

    const followUp = this.#pendingFollowUp();
    if (followUp) {
      payload.pendingFollowUp = followUp;
    }

    return payload;
  }

  /**
   * Met à jour les entités (dernière tâche/projet) à partir du `data` d'un résultat d'exécution
   */
  setEntitiesFromResult(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const record = data as Record<string, unknown>;
    let changed = false;

    if (typeof record['taskId'] === 'string') {
      const title = typeof record['title'] === 'string'
        ? record['title']
        : this.#entities().lastTask?.title ?? '';
      const number = typeof record['taskNumber'] === 'number' ? record['taskNumber'] : undefined;

      this.#entities.update(current => ({
        ...current,
        lastTask: { id: record['taskId'] as string, number, title }
      }));
      changed = true;
    }

    if (typeof record['projectId'] === 'string') {
      const name = typeof record['name'] === 'string'
        ? record['name']
        : this.#entities().lastProject?.name ?? '';
      const identifier = typeof record['identifier'] === 'string' ? record['identifier'] : undefined;

      this.#entities.update(current => ({
        ...current,
        lastProject: { id: record['projectId'] as string, name, identifier }
      }));
      changed = true;

      // L'utilisateur vient de reparler explicitement d'un projet : un éventuel
      // "contexte page ignoré" de type 'project' n'a plus lieu d'être (cf. JSDoc
      // de `#ignoredPageContext`). Pas d'équivalent pour 'calendar' — aucune
      // entité de session ne matérialise un "dernier calendrier mentionné".
      this.#ignoredPageContext.update(ignored => {
        if (!ignored.has('project')) return ignored;
        const next = new Set(ignored);
        next.delete('project');
        return next;
      });
    }

    if (changed) {
      this.#persistSessionPatch({ entities: this.#entities() });
    }
  }

  /**
   * Retire une entité de session (croix sur un chip de contexte "source
   * conversation" — `entities.lastProject`/`entities.lastTask`, ou l'un des
   * 4 types posés par `session.setContext` (Lot 3) : `person`/`group`/
   * `release`/`dueDate` — cf. `VoiceContextChipsComponent`, MOKA-206 Lot F).
   * Ces 4 derniers types n'ont PAS d'équivalent "source page" (contrairement
   * à `lastProject`/calendrier) : ils ne sont posés QUE par la conversation,
   * donc toujours effacés pour de bon au clic, jamais mutés — cf. JSDoc de
   * `VoiceContextChipsComponent`. Même pattern que `clearPendingFollowUp()` :
   * mutation immuable du signal + persistance.
   */
  clearEntity(key: 'lastProject' | 'lastTask' | 'person' | 'group' | 'release' | 'dueDate'): void {
    this.#entities.update(current => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    this.#persistSessionPatch({ entities: this.#entities() });
  }

  /**
   * Pose une entité de contexte CONVERSATION (personne/sprint/release),
   * résolue par `SessionVoiceHandler` à partir de `session.setContext`
   * (MOKA-207 Phase 4, Lot 3). Même mécanique que `setEntitiesFromResult`
   * (mutation immuable + persistance fire-and-forget) mais pour un champ
   * ciblé plutôt que dérivé d'un résultat d'exécution.
   */
  setContextEntity(key: 'person' | 'group' | 'release', entity: SessionContextEntity): void {
    this.#entities.update(current => ({ ...current, [key]: entity }));
    this.#persistSessionPatch({ entities: this.#entities() });
  }

  /**
   * Pose l'échéance de contexte (chaîne `YYYY-MM-DD`, cf. `SessionVoiceHandler`
   * et JSDoc de `SessionEntities.dueDate`).
   */
  setDueDateContext(dueDate: string): void {
    this.#entities.update(current => ({ ...current, dueDate }));
    this.#persistSessionPatch({ entities: this.#entities() });
  }

  /**
   * Écarte un type de contexte de PAGE (croix sur un chip "source page" —
   * projet affiché sans `entities.lastProject`, ou calendrier — cf.
   * `VoiceContextChipsComponent`). Volatile, cf. JSDoc de `#ignoredPageContext`.
   * Le chip correspondant reste affiché, muté (cf. `VoiceContextChipsComponent`)
   * — `reinstatePageContext` en est le retour arrière (clic sur le chip muté).
   */
  ignorePageContext(type: 'project' | 'calendar'): void {
    this.#ignoredPageContext.update(ignored => {
      if (ignored.has(type)) return ignored;
      const next = new Set(ignored);
      next.add(type);
      return next;
    });
  }

  /**
   * Réactive un type de contexte de PAGE précédemment écarté (clic sur un
   * chip muté — cf. `VoiceContextChipsComponent`). Symétrique de
   * `ignorePageContext`.
   */
  reinstatePageContext(type: 'project' | 'calendar'): void {
    this.#ignoredPageContext.update(ignored => {
      if (!ignored.has(type)) return ignored;
      const next = new Set(ignored);
      next.delete(type);
      return next;
    });
  }

  /**
   * Enregistre une question de suivi en attente (ou la retire avec `null`)
   */
  setPendingFollowUp(followUp: PendingFollowUp | null): void {
    this.#pendingFollowUp.set(followUp);
    this.#persistSessionPatch({ pendingFollowUp: followUp });
  }

  clearPendingFollowUp(): void {
    this.#pendingFollowUp.set(null);
    this.#persistSessionPatch({ pendingFollowUp: null });
  }

  /**
   * Suggère un titre de session issu d'un flow guidé (MOKA-207 Phase 4) —
   * label de l'action (ex. "Créer une tâche"), éventuellement enrichi du
   * contexte résolu (ex. "Créer une tâche — Refonte site"). Peut être appelée
   * plusieurs fois pour affiner le titre au fil du flow. No-op dès qu'une
   * saisie SIGNIFICATIVE (texte tapé/transcript vocal) a verrouillé le titre —
   * cf. `#titleLocked`, qui prime toujours sur une suggestion de flow.
   */
  suggestFlowTitle(title: string): void {
    if (this.#titleLocked) return;

    const truncated = truncate(title, MAX_SESSION_TITLE_CHARS);
    this.#pendingFlowTitle = truncated;

    const existingId = this.#persistedSessionId();
    if (existingId !== null) {
      this.#store.updateSession(existingId, { title: truncated })
        .catch(err => console.warn('[VoiceSessionStore] Échec de mise à jour du titre', err));
      return;
    }

    if (this.#sessionCreation) {
      const generation = this.#generation;
      this.#sessionCreation
        .then(sessionId => {
          if (this.#generation !== generation || this.#titleLocked) return;
          return this.#store.updateSession(sessionId, { title: truncated });
        })
        .catch(err => console.warn('[VoiceSessionStore] Échec de mise à jour du titre', err));
    }
    // Sinon : aucune session ni création en cours — `#pendingFlowTitle` sera
    // repris tel quel par `#persistMessage` au premier message `user` (cf. c).
  }

  /**
   * Réinitialise complètement la session locale et détache la session
   * persistée courante (toute Promise de création en vol voit sa résolution
   * ignorée grâce à `#generation`).
   */
  reset(): void {
    this.#generation++;
    this.#messages.set([]);
    this.#entities.set({});
    this.#pendingFollowUp.set(null);
    this.#persistedSessionId.set(null);
    this.#sessionCreation = null;
    // Nouvelle conversation = contexte de page pleinement réautorisé.
    this.#ignoredPageContext.set(new Set());
    // Nouvelle conversation = état de titre vierge.
    this.#titleLocked = false;
    this.#pendingFlowTitle = null;
  }

  /**
   * Démarre explicitement une nouvelle conversation (action utilisateur
   * « Nouvelle conversation »). Fonctionnellement identique à `reset()` —
   * méthode dédiée pour un point d'entrée UI clair et nommé.
   */
  startNewConversation(): void {
    this.reset();
  }

  /**
   * Reprend une session persistée : restaure messages/entités/follow-up
   * depuis le back, puis fait de cette session la session courante (les
   * prochains messages s'y ajoutent, aucune nouvelle session n'est créée).
   * Contrairement aux écritures fire-and-forget, l'échec remonte à
   * l'appelant : l'UI de reprise doit pouvoir afficher un état d'erreur.
   */
  async loadSession(sessionId: string): Promise<void> {
    const [messageRows, sessionRow] = await Promise.all([
      this.#store.getSessionMessages(sessionId),
      this.#store.getSessionById(sessionId)
    ]);

    // Rupture de continuité : toute création en vol lancée avant ce chargement
    // ne doit plus pouvoir écraser la session que l'on s'apprête à restaurer.
    this.#generation++;
    this.#sessionCreation = null;

    this.#messages.set(mapVoiceMessageRowsToChatMessages(messageRows));
    this.#entities.set(sessionRow ? mapVoiceSessionRowToEntities(sessionRow) : {});
    this.#pendingFollowUp.set(sessionRow ? mapVoiceSessionRowToPendingFollowUp(sessionRow) : null);
    this.#persistedSessionId.set(sessionId);
    // Un titre déjà en base ne doit jamais être écrasé par une suite de la
    // conversation reprise ; sinon (session jamais titrée) laisse la porte
    // ouverte à un futur `suggestFlowTitle`/saisie significative.
    this.#titleLocked = !!sessionRow?.title;
    this.#pendingFlowTitle = null;
  }

  /**
   * Liste les sessions récentes du profil courant (panneau d'historique).
   * Échec laissé à l'appelant (gère ses propres états loading/erreur).
   */
  async fetchHistory(): Promise<VoiceSessionSummary[]> {
    return this.#store.listSessions();
  }

  /**
   * Supprime une session persistée (RGPD, action utilisateur explicite depuis
   * le panneau d'historique). Contrairement aux écritures fire-and-forget de
   * ce service (cf. doc de classe), l'échec remonte à l'appelant — même
   * logique que `loadSession()` : une suppression demandée explicitement doit
   * pouvoir afficher un état d'erreur, pas disparaître silencieusement dans
   * un `console.warn`.
   * Si la session supprimée est la session active (`#persistedSessionId`),
   * on repart sur une conversation neuve via `reset()` pour ne pas laisser
   * l'UI continuer d'écrire dans une session qui n'existe plus côté back.
   */
  async deleteSession(sessionId: string): Promise<void> {
    await this.#store.deleteSession(sessionId);

    if (sessionId === this.#persistedSessionId()) {
      this.reset();
    }
  }

  /**
   * Point d'entrée unique de la persistance des messages : lazy creation de
   * session au premier message `user`, sinon insertion dans la session déjà
   * (ou en cours d'être) persistée. Fire-and-forget, catch silencieux — voir
   * doc de classe.
   */
  #persistMessage(
    role: 'user' | 'assistant',
    content: string,
    inputMode: 'voice' | 'text',
    titleEligible: boolean
  ): void {
    const existingId = this.#persistedSessionId();

    if (existingId !== null) {
      this.#insertMessage(existingId, role, content, inputMode);
      if (role === 'user' && titleEligible && !this.#titleLocked) {
        this.#titleLocked = true;
        this.#store.updateSession(existingId, { title: truncate(content, MAX_SESSION_TITLE_CHARS) })
          .catch(err => console.warn('[VoiceSessionStore] Échec de mise à jour du titre', err));
      }
      return;
    }

    const creation = role === 'user' && this.#sessionCreation === null
      ? this.#createSession(
          titleEligible ? truncate(content, MAX_SESSION_TITLE_CHARS) : this.#pendingFlowTitle,
          titleEligible
        )
      : this.#sessionCreation;

    if (!creation) return;

    creation
      .then(sessionId => this.#insertMessage(sessionId, role, content, inputMode))
      .catch(() => {
        // Déjà logué par #createSession ; le message reste dans #messages,
        // jamais perdu pour l'UI locale.
      });
  }

  /**
   * Crée la session persistée (fire-and-forget vis-à-vis de l'appelant) et
   * mémorise la Promise en vol pour que les messages suivants s'y enchaînent.
   */
  #createSession(title: string | null, titleEligible: boolean): Promise<string> {
    const generation = this.#generation;
    this.#titleLocked = titleEligible;

    const creation = this.#store.createSession(title, this.#entities())
      .then(row => {
        if (this.#generation === generation) {
          this.#persistedSessionId.set(row.id);
        }
        return row.id;
      })
      .catch(err => {
        console.warn('[VoiceSessionStore] Échec de création de session', err);
        if (this.#generation === generation) {
          this.#sessionCreation = null;
        }
        throw err;
      });

    this.#sessionCreation = creation;
    return creation;
  }

  #insertMessage(
    sessionId: string,
    role: 'user' | 'assistant',
    content: string,
    inputMode: 'voice' | 'text'
  ): void {
    this.#store.insertMessage(sessionId, role, content, inputMode)
      .catch(err => console.warn('[VoiceSessionStore] Échec d\'insertion du message', err));
  }

  /**
   * Met à jour la session persistée (entités/follow-up) si elle existe déjà.
   * No-op tant qu'aucun message `user` n'a encore créé de session.
   */
  #persistSessionPatch(patch: { entities?: SessionEntities; pendingFollowUp?: PendingFollowUp | null }): void {
    const sessionId = this.#persistedSessionId();
    if (sessionId === null) return;

    this.#store.updateSession(sessionId, patch)
      .catch(err => console.warn('[VoiceSessionStore] Échec de mise à jour de la session', err));
  }
}
